import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "design-system/atoms/Collapsible"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Separator } from "design-system/atoms/Separator"
import { Textarea } from "design-system/atoms/Textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { getErrorTranslations } from "lib/i18n"
import { isNil } from "lopermut"
import { ChevronDown, Plus, X } from "lucide-react"
import { useCreateGoalMutation } from "modules/campaigns/hooks/sections/goals/useCreateGoalMutation"
import { useGoalQuery } from "modules/campaigns/hooks/sections/goals/useGoalQuery"
import { useUpdateGoalMutation } from "modules/campaigns/hooks/sections/goals/useUpdateGoalMutation"
import { Loading } from "modules/shared/components/Loading"
import { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateGoalSchema, GoalMediaStrategyEnum, GoalType, GoalTypeEnum } from "schema"
import { z } from "zod"
import { GoalMedia } from "./GoalMedia"
import { GoalMediaStrategySelect } from "./GoalMediaStrategySelect"
import { GoalTypeSelect } from "./GoalTypeSelect"

type Props = {
  id?: string
  open?: boolean
  onClose?: () => void
}

export const UpsertGoalModal = ({ id, open, onClose: onCloseProp }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const form = useForm<z.infer<typeof CreateGoalSchema>>({
    resolver: zodResolver(CreateGoalSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      assistantInstructions: "",
      insightsInstructions: "",
      type: GoalTypeEnum.Classification,
      goalOptions: [],
      shots: undefined,
      mediaStrategy: GoalMediaStrategyEnum.ShareAll,
      isUnderlying: false,
    },
  })

  const { data: goalData, isPending: isGoalLoading } = useGoalQuery(id ?? null)
  const { mutateAsync: createGoal, isPending: isCreatingGoal } = useCreateGoalMutation()
  const { mutateAsync: updateGoal, isPending: isUpdatingGoal } = useUpdateGoalMutation()

  // Local UI state for creating options
  const [optionInputValue, setOptionInputValue] = useState("")
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)

  const options = form.watch("goalOptions") ?? []
  const selectedType = form.watch("type")
  const isNpsActive = selectedType === GoalTypeEnum.NPS
  const isPredefinedActive = selectedType === GoalTypeEnum.Predefined

  const onOpenChange = (isOpen: boolean = false) => {
    if (!isOpen) {
      if (onCloseProp) {
        onCloseProp()
      } else {
        navigate({ to: "/campaigns/goals/list", search: { page: 1, field: "name", order: "asc" } })
      }
    }
  }

  const onSubmit = async (payload: z.infer<typeof CreateGoalSchema>) => {
    if (isEdit && id) {
      await updateGoal({ id, payload })
    } else {
      await createGoal(payload as z.infer<typeof CreateGoalSchema>)
    }
    onOpenChange()
  }

  useEffect(() => {
    if (goalData) {
      form.reset({
        name: goalData.displayName ?? goalData.name,
        assistantInstructions: goalData.assistantInstructions ?? undefined,
        insightsInstructions: goalData.insightsInstructions ?? undefined,
        type: goalData.type,
        goalOptions: goalData.goalOptions?.map((o) => o.value) ?? [],
        shots: goalData.shots ?? undefined,
        goalMedias: goalData.goalMedias ?? [],
        mediaStrategy: goalData.mediaStrategy ?? GoalMediaStrategyEnum.ShareAll,
        isUnderlying: goalData.isUnderlying ?? false,
      })
    }
  }, [goalData, form])

  const addOption = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed || isNpsActive) return
    const current = (form.getValues("goalOptions") ?? []) as string[]
    if (current.some((o) => o.toLowerCase() === trimmed.toLowerCase())) return
    const updated = [...current, trimmed]
    form.setValue("goalOptions", updated, { shouldDirty: true, shouldValidate: true })
    form.setValue("type", GoalTypeEnum.Predefined, { shouldDirty: true, shouldValidate: true })
    setOptionInputValue("")
  }

  const removeOption = (optionToRemove: string) => {
    const current = (form.getValues("goalOptions") ?? []) as string[]
    const updated = current.filter((o) => o !== optionToRemove)
    form.setValue("goalOptions", updated, { shouldDirty: true, shouldValidate: true })
    if (updated.length === 0) {
      form.setValue("type", GoalTypeEnum.Open, { shouldDirty: true, shouldValidate: true })
    }
  }

  // Track previous type to handle transitions out of NPS
  const prevTypeRef = useRef<GoalType | undefined>(undefined)

  // Sync options with selected type
  useEffect(() => {
    if (!selectedType) return
    const prevType = prevTypeRef.current

    // Entering NPS → set NPS options (0-10)
    if (selectedType === GoalTypeEnum.NPS && prevType !== GoalTypeEnum.NPS) {
      form.setValue(
        "goalOptions",
        Array.from({ length: 11 }, (_, i) => String(i)),
        { shouldDirty: true, shouldValidate: true },
      )
    }

    // Leaving NPS → clear options
    if (prevType === GoalTypeEnum.NPS && selectedType !== GoalTypeEnum.NPS) {
      form.setValue("goalOptions", [], { shouldDirty: true, shouldValidate: true })
    }

    prevTypeRef.current = selectedType
  }, [selectedType, form])

  return (
    <Dialog open={isNil(open) ? true : open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <DialogTitle>
              {isEdit
                ? t("campaigns.goalsManagement.modal.editTitle")
                : t("campaigns.goalsManagement.modal.createTitle")}
            </DialogTitle>

            {goalData?.isSystemGoal && (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline">{t("campaigns.goalsManagement.modal.default")}</Badge>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {t("campaigns.goalsManagement.modal.defaultTooltip")}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </DialogHeader>
        <fieldset disabled={!!goalData?.isSystemGoal}>
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("campaigns.goalsManagement.modal.fields.name")}</FormLabel>
                  <FormControl>
                    <Input {...field} required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="assistantInstructions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Tooltip>
                      <TooltipTrigger slick>
                        {t("campaigns.goalsManagement.modal.fields.assistantInstructions")}
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        {t("campaigns.goalsManagement.modal.fields.assistantInstructionsTooltip")}
                      </TooltipContent>
                    </Tooltip>
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {(selectedType === GoalTypeEnum.Open ||
              selectedType === GoalTypeEnum.Classification) && (
              <FormField
                control={form.control}
                name="insightsInstructions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Tooltip>
                        <TooltipTrigger slick>
                          {t("campaigns.goalsManagement.modal.fields.insightsInstructions")}
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                          {t("campaigns.goalsManagement.modal.fields.insightsInstructionsTooltip")}
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("campaigns.goalsManagement.modal.fields.type")}</FormLabel>
                  <FormControl>
                    <GoalTypeSelect
                      value={field.value}
                      onValueChange={(value) => {
                        form.setValue("type", value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                        if (value !== GoalTypeEnum.Open && value !== GoalTypeEnum.Classification) {
                          form.setValue("isUnderlying", false, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                      }}
                      disabled={isEdit}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {(isPredefinedActive || isNpsActive) && (
              <FormField
                control={form.control}
                name="goalOptions"
                render={() => (
                  <FormItem>
                    <FormLabel>{t("campaigns.goals.options")}</FormLabel>
                    <div className="flex flex-col gap-2">
                      {isPredefinedActive && (
                        <div className="flex items-end gap-2">
                          <Input
                            value={optionInputValue}
                            disabled={isNpsActive}
                            placeholder={t("campaigns.goals.addOption")}
                            onChange={(e) => setOptionInputValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                addOption(optionInputValue)
                              }
                              if (e.key === "Escape") {
                                setOptionInputValue("")
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={isNpsActive}
                            onClick={() => addOption(optionInputValue)}
                            className="w-10 h-10"
                          >
                            <Plus className="size-4" />
                          </Button>
                        </div>
                      )}

                      {options.length > 0 && (
                        <div className="flex flex-row flex-wrap gap-2">
                          {options.map((opt) => (
                            <Badge
                              key={opt}
                              variant="secondary"
                              className={cn("flex items-center")}
                            >
                              {opt}
                              {isPredefinedActive && !isEdit && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="w-6 h-6 ml-1"
                                  onClick={() => removeOption(opt)}
                                >
                                  <X className="size-4" />
                                </Button>
                              )}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </FormItem>
                )}
              />
            )}
            <Collapsible open={isAdvancedOpen} onOpenChange={setIsAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  className="flex items-center gap-2 w-full justify-between px-0 hover:bg-transparent"
                >
                  <span className="text-sm font-medium text-muted-foreground">
                    {t("campaigns.goals.advancedSettings")}
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      isAdvancedOpen && "rotate-180",
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="flex flex-col gap-4">
                <Separator />
                {(selectedType === GoalTypeEnum.Open ||
                  selectedType === GoalTypeEnum.Classification) && (
                  <FormField
                    control={form.control}
                    name="isUnderlying"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex flex-row items-center gap-2">
                          <Checkbox
                            id="isUnderlying"
                            checked={Boolean(field.value)}
                            onCheckedChange={field.onChange}
                          />
                          <FormLabel htmlFor="isUnderlying">
                            {t("campaigns.goals.underlying")}
                          </FormLabel>
                        </div>
                        <FormDescription>
                          {t("campaigns.goals.underlyingDescription")}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <GoalMedia form={form} />
                <FormField
                  control={form.control}
                  name="mediaStrategy"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("campaigns.goals.mediaStrategy.label")}</FormLabel>
                      <FormControl>
                        <GoalMediaStrategySelect
                          value={field.value}
                          onValueChange={field.onChange}
                        />
                      </FormControl>
                      <FormDescription>
                        {t("campaigns.goals.mediaStrategy.description")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CollapsibleContent>
            </Collapsible>
          </Form>
          <DialogFooter>
            <div className="flex justify-end w-full mt-6">
              <div className="flex items-center gap-2">
                {(isCreatingGoal || isUpdatingGoal) && <Loading minimal />}
                <Button
                  disabled={(isEdit && isGoalLoading) || !form.formState.isValid}
                  onClick={form.handleSubmit(onSubmit)}
                >
                  {isEdit ? t("shared.save") : t("shared.create")}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </fieldset>
      </DialogContent>
    </Dialog>
  )
}
